import { create } from 'zustand';
import { CartItem } from 'types/cart';
import { devtools } from 'zustand/middleware';
import { DiscountData, PricingFormState } from 'types/purchaseMembership';
import { getPlan } from 'utilities/subscriptionPlanHelper';
type State = {
  initialized: boolean;
  activeStep: number;
  items: CartItem[];
  agreedToTermsAndConditions: boolean;
  tax: number;
  total: number;
  orderCompletionDate: Date | undefined;
  orderEmail: string;
  invoiceNumber: string;
  form: PricingFormState;
  discount: DiscountData;
  autoRenew: boolean;
};

type Actions = {
  setActiveStep: (step: number) => void;
  addItemToCart: (item: CartItem) => void;
  removeItemFromCart: (itemId: string) => void;
  setInitialized: (initialized: boolean) => void;
  setAgreedToTermsAndConditions: (agreed: boolean) => void;
  setTax: (tax: number) => void;
  setTotal: (total: number) => void;
  setOrderCompletionDate: (date: string) => void;
  setOrderEmail: (email: string) => void;
  setInvoiceNumber: (invoiceNumber: string) => void;
  setForm: (form: PricingFormState) => void;
  setAutoRenew: (autoRenew: boolean) => void;
  setDiscount: (discount: DiscountData) => void;
  reset: () => void;
};

export const useCartStore = create<State & Actions>()(
  devtools(
    (set, get, store) => ({
      initialized: false,
      activeStep: 0,
      items: [],
      agreedToTermsAndConditions: false,
      tax: 0,
      total: 0,
      orderCompletionDate: undefined,
      orderEmail: '',
      invoiceNumber: '',
      form: {
        plan: getPlan(get()?.items, get()?.autoRenew),
        coupon: '',
        billingCountry: '',
        billingPostalCode: '',
        billingVatNumber: '',
        email: '',
        first_name: '',
        last_name: '',
        address1: '',
        address2: '',
        city: '',
        country: '',
        state: '',
        postal_code: '',
      },
      autoRenew: false,
      discount: {
        couponCode: '',
        discount: '',
        currencySymbol: '',
      },
      setActiveStep: (step: number) => set(() => ({ activeStep: step }), undefined, 'setActiveStep'),
      setInitialized: (initialized: boolean) => set(() => ({ initialized }), undefined, 'setInitialized'),
      addItemToCart: (item: CartItem) =>
        set(
          (state) => ({ items: [...state.items, item], form: { ...state.form, plan: getPlan([...state.items, item], state.autoRenew) } }),
          undefined,
          'addItemToCart'
        ),
      removeItemFromCart: (itemId: string) =>
        set(
          (state) => ({
            items: state.items.filter((item) => item.id !== itemId),
            form: {
              ...state.form,
              plan: getPlan(
                state.items.filter((item) => item.id !== itemId),
                state.autoRenew
              ),
            },
          }),
          undefined,
          'removeItemFromCart'
        ),
      reset: () => set(store.getInitialState(), undefined, 'reset'),
      setAgreedToTermsAndConditions: (agreed: boolean) =>
        set(() => ({ agreedToTermsAndConditions: agreed }), undefined, 'setAgreedToTermsAndConditions'),
      setTax: (tax: number) => set(() => ({ tax }), undefined, 'setTax'),
      setTotal: (total: number) => set(() => ({ total }), undefined, 'setTotal'),
      setOrderCompletionDate: (orderCompletionDate: string) =>
        set(() => ({ orderCompletionDate: new Date(orderCompletionDate) }), undefined, 'setOrderCompletionDate'),
      setOrderEmail: (orderEmail: string) => set(() => ({ orderEmail }), undefined, 'setOrderEmail'),
      setInvoiceNumber: (invoiceNumber: string) => set(() => ({ invoiceNumber }), undefined, 'setInvoiceNumber'),
      setForm: (form: PricingFormState) => set(() => ({ form }), undefined, 'setForm'),
      setAutoRenew: (autoRenew: boolean) =>
        set((state) => ({ autoRenew, form: { ...state.form, plan: getPlan(state.items, autoRenew) } }), undefined, 'setAutoRenew'),
      setDiscount: (discount: DiscountData) => set(() => ({ discount }), undefined, 'setDiscount'),
    }),
    { name: 'CartStore' }
  )
);
